import React from 'react';
import { Box, Button, IconButton, Typography, Paper } from '@mui/material';
import { Add, Delete, DragIndicator } from '@mui/icons-material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { QuestionnaireFormData } from '@/features/questionnaires/types/questionnaire.interface';
import { useTranslation } from 'react-i18next';
import type { QuestionOption } from '@aphp-ema/common/questionnaire';
import { OptionsCsvUploader } from './OptionsCsvUploader';
import { TextInput } from '@/shared';

type OptionsManagerProps = {
  questionIndex: number;
  isDisabled: boolean;
};

type SortableOptionProps = {
  field: { _id: string; label: string };
  index: number;
  questionIndex: number;
  isDisabled: boolean;
  onRemove: (index: number) => void;
  fieldsLength: number;
};

const SortableOption = ({
  field,
  index,
  questionIndex,
  isDisabled,
  onRemove,
  fieldsLength,
}: SortableOptionProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field._id });
  const form = useFormContext<QuestionnaireFormData>();

  const { t } = useTranslation();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton
          size="small"
          disabled={isDisabled}
          sx={{
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
          {...attributes}
          {...listeners}
        >
          <DragIndicator />
        </IconButton>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            pt: 1,
          }}
        >
          <TextInput
            form={form}
            name={`questions.${questionIndex}.parameters.options.${index}.label`}
            label={t('pages.studies.questions.options.label')}
            disabled={isDisabled}
            fullWidth
            required
            onChange={() => {
              form.trigger(`questions.${questionIndex}.parameters.options`);
            }}
          />
        </Box>

        <IconButton
          onClick={() => onRemove(index)}
          disabled={isDisabled || fieldsLength <= 1}
          color="error"
          size="small"
          sx={{ mt: 1 }}
        >
          <Delete />
        </IconButton>
      </Box>
    </Paper>
  );
};

export const OptionsManager = ({
  questionIndex,
  isDisabled,
}: OptionsManagerProps) => {
  const form = useFormContext<QuestionnaireFormData>();
  const { control } = form;
  const { t } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `questions.${questionIndex}.parameters.options`,
    keyName: '_id',
  });

  // Ensure we have at least one option
  React.useEffect(() => {
    if (fields.length === 0) {
      append({ label: '' });
    }
  }, [fields.length, append]);

  const handleAddOption = () => {
    append({ label: '' });
  };

  const handleCsvOptionsUploaded = (newOptions: QuestionOption[]) => {
    const replaceOptions = () => {
      const currentLength = fields.length;
      for (let i = currentLength - 1; i >= 0; i--) {
        remove(i);
      }

      newOptions.forEach((option) => {
        append({ label: option.label });
      });
    };

    replaceOptions();
    form.trigger(`questions.${questionIndex}.parameters.options`);
  };

  const handleRemoveOption = (index: number) => {
    remove(index);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex((field) => field._id === active.id);
      const newIndex = fields.findIndex((field) => field._id === over?.id);

      move(oldIndex, newIndex);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">
          {t('pages.studies.questions.options.title')}
        </Typography>
        <OptionsCsvUploader
          onOptionsUploaded={handleCsvOptionsUploaded}
          disabled={isDisabled}
        />
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={fields.map((field) => field._id)}
          strategy={verticalListSortingStrategy}
        >
          {fields.map((field, index) => (
            <SortableOption
              key={field._id}
              field={field}
              index={index}
              questionIndex={questionIndex}
              isDisabled={isDisabled}
              onRemove={handleRemoveOption}
              fieldsLength={fields.length}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={handleAddOption}
        disabled={isDisabled}
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('pages.studies.questions.options.addOption')}
      </Button>
    </Box>
  );
};
